import React, { useEffect, useState } from 'react';
import { Video, User, Calendar as CalendarIcon } from 'lucide-react';
import type { ResolvedSyncSettings } from './types';
import { TYPES_WITH_ATTENDEES } from './eventData';
import { AttendeesPicker, type AttendeeEntry, type AttendeeUserOption } from './AttendeesPicker';
import {
  MeetingProviderSelect,
  MeetingProviderNotices,
  type MeetingProvider,
} from './MeetingProviderSelect';
import { resolveSyncSwitch } from './syncSwitch';
import { SectionToggle } from './ui/SectionToggle';
import { PropRow } from './ui/PropRow';
import { DrawerCallout } from './ui/DrawerCallout';
import { SwitchRow } from './ui/SwitchRow';
import { usePermissions } from '../../../lib/hooks/usePermissions';

export interface OrganizerCandidate {
  userId: string;
  label: string;
  /** '' quando il candidato non ha Google Calendar connesso → pulsante disabilitato. */
  calendarId: string;
}

const TYPES_WITH_ATTENDEES_SET = new Set<string>(TYPES_WITH_ATTENDEES);

export interface CalendarSectionProps {
  activityType: string;
  syncOptIn: boolean;
  onSyncOptInChange(value: boolean): void;
  settings: ResolvedSyncSettings | null;
  meetingProvider: MeetingProvider;
  onMeetingProviderChange(provider: MeetingProvider): void;
  attendees: AttendeeEntry[];
  onAttendeesChange(attendees: AttendeeEntry[]): void;
  organizerCandidates: OrganizerCandidate[];
  organizerId: string;
  onOrganizerChange(userId: string): void;
  /** Toggle "Mostra descrizione nell'evento" — spostato qui dalla shell (redesign T5). */
  includeDescriptionInEvent: boolean;
  onIncludeDescriptionChange(value: boolean): void;
  /**
   * `editingActivity.google_event_id` PERSISTITO (null in creazione): serve solo ad
   * avvisare l'utente quando salvare sposterà l'evento da Google a Outlook.
   */
  existingGoogleEventId?: string | null;
  /**
   * Aggiunte rispetto al contratto del piano — vedi task-9-report.md per il perché:
   * impostazione org-level per Teams (non presente in ResolvedSyncSettings), lista utenti
   * CRM per l'autocomplete invitati, contatti del cliente/lead per le chip, e flag di
   * caricamento durante la creazione del meeting (fonte: LeadStatusUpdateDrawer.tsx:2260-2265).
   */
  teamsServiceEnabled: boolean;
  users: AttendeeUserOption[];
  availableContacts: AttendeeEntry[];
  creatingMeet?: boolean;
}

/** Nome completo dell'utente per i tooltip delle pill organizzatore (fallback: la label). */
function organizerTooltipName(users: AttendeeUserOption[], candidate: OrganizerCandidate): string {
  const user = users.find((u) => u.id === candidate.userId);
  return user?.full_name || user?.email || candidate.label;
}

/**
 * Sezione "Calendario e invitati" del drawer Attività/Nota, come gruppo richiudibile
 * (`SectionToggle`, mockup viste 2 e 4): switch di sync provider-aware, righe di proprietà
 * per provider meeting e organizzatore, picker invitati, switch descrizione nell'evento.
 *
 * Componente CONTROLLATO: nessuna query interna, tutti i dati arrivano via props dalla
 * shell (`ActivityNoteDrawer`, che la rende DOPO l'editor — ordine del redesign).
 *
 * Redesign T5: ECCEZIONE FUNZIONALE B1 (switch visibile e pre-spuntato anche per un
 * assegnatario Microsoft-only, vedi `syncSwitch.ts`); M1 (sunto nel titolo del toggle, che
 * assorbe il badge verde sulla riga "Data e ora"); M4 (hint "Genera link Meet" come suffisso
 * ghost della riga Provider, spinner come riga di stato); M5 (select "Calendario" rimossa,
 * era UI morta con `availableCalendars` sempre `[]`); B2 (riga Provider solo per
 * videochiamata, riga Organizzatore solo con 2 candidati, messaggi Teams come callout).
 */
export function CalendarSection({
  activityType,
  syncOptIn,
  onSyncOptInChange,
  settings,
  meetingProvider,
  onMeetingProviderChange,
  attendees,
  onAttendeesChange,
  organizerCandidates,
  organizerId,
  onOrganizerChange,
  includeDescriptionInEvent,
  onIncludeDescriptionChange,
  existingGoogleEventId,
  teamsServiceEnabled,
  users,
  availableContacts,
  creatingMeet,
}: CalendarSectionProps) {
  const google = settings?.google ?? { enabled: false, calendarId: null, connected: false };
  const microsoft = settings?.microsoft ?? { enabled: false, calendarId: null, connected: false };
  const syncSwitch = resolveSyncSwitch(
    { enabled: google.enabled, calendarId: google.calendarId },
    { enabled: microsoft.enabled, connected: microsoft.connected, calendarId: microsoft.calendarId }
  );
  const showAttendeeFields = TYPES_WITH_ATTENDEES_SET.has(activityType);
  // "Sincronizzazione non attiva" solo a chi puo' collegare Google o Outlook (permesso calendar.sync)
  const { hasPermission } = usePermissions();
  const canSyncCalendar = hasPermission('calendar', 'sync');
  const isVideoCall = activityType === 'videochiamata';

  // Edit con evento Google persistito ma assegnatario senza Google configurato: al
  // salvataggio l'evento viene spostato su Outlook (delete Google + create Microsoft,
  // in quest'ordine e solo se Microsoft scrive davvero — vedi useActivitySave).
  // Stesso predicato di isMoveToMicrosoft (useActivitySave): il warning deve accendersi
  // esattamente quando il salvataggio farebbe il move, non su una condizione piu' larga.
  const googleConfigured = !!google.enabled;
  const showMoveToOutlookWarning = !!existingGoogleEventId && syncSwitch.visible && !googleConfigured && syncOptIn;

  // Aperta di default per i tipi con invitati (dove ci sono davvero campi da compilare),
  // richiudibile a mano; il default si riallinea quando il tipo attività cambia natura.
  const [open, setOpen] = useState(showAttendeeFields);
  useEffect(() => {
    setOpen(showAttendeeFields);
  }, [showAttendeeFields]);

  const syncActive = syncSwitch.visible && syncOptIn;
  const attendeesCount = showAttendeeFields ? attendees.length : 0;
  const summary = (
    <span
      className={`ml-auto inline-flex items-center gap-1.5 text-[11px] ${
        syncActive ? 'text-[#0F7B6C] dark:text-[#4FBFA8]' : 'text-[#787774] dark:text-gray-400'
      }`}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: syncActive ? '#0F7B6C' : '#9B9A97' }}
      />
      {syncActive ? 'Sync attiva' : 'Sync non attiva'}
      {attendeesCount > 0 && ` · ${attendeesCount} invitat${attendeesCount === 1 ? 'o' : 'i'}`}
    </span>
  );

  return (
    <SectionToggle
      open={open}
      onToggle={() => setOpen((prev) => !prev)}
      icon={<CalendarIcon className="h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />}
      title={showAttendeeFields ? 'Calendario e invitati' : 'Calendario'}
      summary={summary}
      dataTour="drawer-calendar"
    >
      {syncSwitch.visible ? (
        <SwitchRow
          id="sync-opt-in-toggle"
          title={syncSwitch.label}
          description="Crea l'evento e genera il link riunione"
          checked={syncOptIn}
          onChange={onSyncOptInChange}
        />
      ) : canSyncCalendar && (
        <DrawerCallout>
          <span className="block font-medium">Sincronizzazione non attiva</span>
          <span className="text-[#787774] dark:text-gray-400">
            L'assegnatario non ha collegato un calendario (Google o Microsoft): l'attività non comparirà sul suo
            calendario. Si attiva dal profilo utente.
          </span>
        </DrawerCallout>
      )}

      {/* Spunta descrizione SUBITO dopo lo switch sync: in coda al drawer non la
          vedeva più nessuno e le note smettevano di arrivare sugli eventi
          (segnalazione RIE 31/08/2026). */}
      {showAttendeeFields && (
        <SwitchRow
          id="include-description-toggle"
          title="Mostra descrizione nell'evento"
          description="Includi la descrizione dell'attività nell'evento del calendario"
          checked={includeDescriptionInEvent}
          onChange={onIncludeDescriptionChange}
        />
      )}

      {showMoveToOutlookWarning && (
        <DrawerCallout tone="warning">
          L'evento Google esistente verrà rimosso e ricreato su Outlook al salvataggio.
        </DrawerCallout>
      )}

      {/* Riga di stato sotto lo switch (M4) — fonte: LeadStatusUpdateDrawer.tsx:2260-2265 */}
      {creatingMeet && (
        <div className="flex items-center gap-2 px-1.5 text-[11px] text-[#787774] dark:text-gray-400">
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-[#2383E2] border-t-transparent" />
          Creazione meet in corso...
        </div>
      )}

      {showAttendeeFields && (
        <div className="flex flex-col">
          {isVideoCall && (
            <PropRow
              icon={<Video className="h-3 w-3" strokeWidth={1.5} />}
              label="Provider meeting"
              trailing={
                syncOptIn && meetingProvider === 'google_meet' ? (
                  <>
                    <Video className="h-3 w-3" strokeWidth={1.5} />
                    Genera link Meet
                  </>
                ) : undefined
              }
            >
              <MeetingProviderSelect
                meetingProvider={meetingProvider}
                onMeetingProviderChange={onMeetingProviderChange}
                googleConnected={google.connected}
                microsoftConnected={microsoft.connected}
                microsoftSyncEnabled={microsoft.enabled}
                teamsServiceEnabled={teamsServiceEnabled}
              />
            </PropRow>
          )}

          {/* Organizzatore: solo con una scelta reale (2 candidati) — B2 */}
          {organizerCandidates.length > 1 && (
            <PropRow icon={<User className="h-3 w-3" strokeWidth={1.5} />} label="Organizzatore">
              <span className="inline-flex rounded-md border border-[rgba(55,53,47,0.16)] dark:border-[#4A5053] p-0.5 bg-white dark:bg-[#373C3F]">
                {organizerCandidates.map((candidate) => {
                  const hasGoogle = !!candidate.calendarId;
                  const isActive = organizerId === candidate.userId && hasGoogle;
                  const name = organizerTooltipName(users, candidate);
                  return (
                    <button
                      key={candidate.userId}
                      type="button"
                      onClick={() => hasGoogle && onOrganizerChange(candidate.userId)}
                      disabled={!hasGoogle}
                      title={hasGoogle ? name : `${name}: Google Calendar non connesso`}
                      className={`px-2.5 py-0.5 text-[11px] rounded transition-colors ${
                        isActive
                          ? 'bg-[#2383E2] text-white'
                          : hasGoogle
                            ? 'text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.08]'
                            : 'text-[#787774] dark:text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {candidate.label}
                    </button>
                  );
                })}
              </span>
            </PropRow>
          )}
        </div>
      )}

      {isVideoCall && showAttendeeFields && (
        <MeetingProviderNotices
          meetingProvider={meetingProvider}
          microsoftConnected={microsoft.connected}
          microsoftSyncEnabled={microsoft.enabled}
          teamsServiceEnabled={teamsServiceEnabled}
        />
      )}

      {showAttendeeFields && (
        <>
          <AttendeesPicker
            attendees={attendees}
            onAttendeesChange={onAttendeesChange}
            users={users}
            availableContacts={availableContacts}
          />
        </>
      )}
    </SectionToggle>
  );
}

export type { AttendeeEntry, AttendeeUserOption } from './AttendeesPicker';
export type { MeetingProvider } from './MeetingProviderSelect';
